import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/auth.service'
import { toast } from 'react-toastify'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState({ email: '' })
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const [isProcessing, setIsProcessing] = useState(false)
  const navigate = useNavigate()

  const validateForm = () => {
    const newErrors = { email: '' }
    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    setErrors(newErrors)
    return !newErrors.email; // Return true if there are no errors
  }

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isEmailSent && countdown > 0) {
      timer = setInterval(() => setCountdown(prev => prev - 1), 1000)
    }
    return () => clearInterval(timer)
  }, [isEmailSent, countdown])

  const handleResetPassword = async () => {
    if (validateForm()) {
      setIsProcessing(true)
      try {
        
        const response = await authService.forgotPassword(email)
        if (response.message === 'User not found') {
          toast.error(response.message);
        }
        
        if (response.message.includes("Password reset link has been sent to your email")) {
          setIsEmailSent(true)
          setCountdown(60)
          toast.success(response.message)
        }
      } catch (error: any) {
        
        console.error('Password Reset Error:', {
          message: error?.message || 'Unknown error',
          status: error?.response?.status,
          data: error?.response?.data,
          email: email,
          timestamp: new Date().toISOString()
        });
        
        const errorMessage = error.message || 'Failed to send reset password email. Please try again.'
        setErrors({ email: errorMessage })
        toast.error(errorMessage)
      } finally {
        setIsProcessing(false)
      }
    } else {
      
      toast.error('Please enter a valid email address');
    }
  }

  const handleResendLink = async () => {
    setIsProcessing(true)
    try {
      
      const response = await authService.forgotPassword(email)
      

      if (response.message === "Password reset link has been sent to your email. The link will expire in 5 minutes.") {
        setIsEmailSent(true)
        setCountdown(60)
        toast.success(response.message)
      } else if (response.message === 'User not found') {
        toast.error('No account found with this email address')
      }
    } catch (error: any) {
      
      console.error('Resend Link Error:', {
        message: error?.message || 'Unknown error',
        status: error?.response?.status,
        data: error?.response?.data,
        email: email,
        timestamp: new Date().toISOString()
      });
      
      const errorMessage = error.message || 'Failed to resend reset password email. Please try again.'
      setErrors({ email: errorMessage })
      toast.error(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const renderEmailSentMessage = () => (
    <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background" 
      />
      <div className="bg-white rounded-card shadow-lg w-full max-w-card  p-card-padding pb-card-padding-bottom gap-section-gap relative z-10">
        <div className="flex flex-col items-center">
          {/* <img src="/ua-canada-logo.png" alt="UA Canada Logo" className="h-12 mb-6" /> */}
          <h1 className="text-heading font-heading text-text-primary mb-2">Reset Password</h1>
          <p className="text-text-secondary text-subtext text-center mb-8">A password reset link has been sent to your email. If you don't receive it in a few minutes, check your spam folder or request a new link below.</p>
        </div>
        <div className="mb-8 text-center">
          <p className="text-[20px] font-[700] text-[#000000]">{countdown}</p>
          <p className="text-text-secondary text-[16px] text-subtext">remaining</p>
        </div>
        <button
          onClick={() => {
            if (countdown > 0) {
              toast.info(`Please wait ${countdown} seconds before requesting another link`);
              return;
            }
            handleResendLink();
          }}
          disabled={countdown > 0 || isProcessing}
          id="resend-link-button"
          className={`w-full btn-primary mb-4 ${countdown > 0 || isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isProcessing ? 'Sending...' : 'Resend Link'}
        </button>
        <p className="text-[16px] text-text-secondary text-center">
          Remember your password?{' '}
          <button onClick={() => navigate('/login')} className="text-small text-text-secondary underline">
            Login
          </button>
        </p>
      </div>
    </div>
  )

  const renderResetPasswordForm = () => (
    <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background" 
      />
      <div className="bg-white rounded-card shadow-lg w-full max-w-card  p-card-padding pb-card-padding-bottom gap-section-gap relative z-10">
        <div className="flex flex-col items-center mb-8">
          <h1 id="forgot-password-title" className="text-heading font-heading text-text-primary text-center mb-2">Enter your registered email and we will send you a password reset link</h1>
        </div>
        <form className="space-y-4">
          <div className="relative">
            <label htmlFor="forgot-password-email" className="block text-[14px] font-medium text-[#767A81] mb-1">
              Email
            </label>
            <input
              id="forgot-password-email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              className="w-full px-3 py-2 text-[16px] border border-border rounded-input text-body focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              placeholder="Enter your email"
            />
            {errors.email && (
              <p id="forgot-password-email-error" className="text-small text-red-600 mt-1">{errors.email}</p>
            )}
          </div>
          <button
            id="forgot-password-submit"
            type="button"
            onClick={handleResetPassword}
            disabled={isProcessing}
           className="w-full btn-primary"
          >
            {isProcessing ? 'Processing...' : 'Send Reset Link'}
          </button>
        </form>


      <p className="mt-4 text-center text-[16px] text-text-secondary">
          Remember your password?{' '}
          <button id="forgot-password-back" onClick={() => navigate('/login')} className="text-small text-text-secondary underline">
            Go back to Login
          </button>
        </p>
      </div>
    </div>
  )

  return isEmailSent ? renderEmailSentMessage() : renderResetPasswordForm()
}
